<template>
  <div class="consent-details-wrapper bg-white">
    <div class="px-4 py-3 border-bottom">
      <div class="d-flex align-items-center gap-2">
        <h5 class="mb-0">{{ getLocalizedContent(consent.title) || consent.name }}</h5>
        <span v-if="consent.required" class="badge bg-danger text-uppercase" style="font-size: 11px; border-radius: 0">
          {{ $t('consents.required') }}
        </span>
      </div>
    </div>

    <div v-if="isAlreadyAnswered" class="px-4 py-2 d-flex align-items-center gap-2" style="background: #f0fae8; border-bottom: 2px solid #58ac27">
      <span class="success-green fw-bold">✓</span>
      <small class="success-green fw-bold">{{ $t('consents.alreadyAnswered') }}</small>
    </div>

    <div v-if="!isAlreadyAnswered && pendingRequiredCount > 0" class="px-4 py-2 d-flex align-items-center gap-2" style="border-bottom: 2px solid #ed1c24; background: #fff5f5">
      <span class="text-danger fw-bold">!</span>
      <small class="text-danger fw-bold">
        {{ $t('consents.requiredQuestionsMustBeAccepted', { count: pendingRequiredCount, plural: pendingRequiredCount > 1 ? 's' : '' }) }}
      </small>
    </div>

    <div
      class="consent-content px-4 py-3"
      :class="{ 'consent-content--modal-mobile': constrainForModal }"
      ref="contentRef"
      @scroll="handleScroll"
    >
      <div v-html="getLocalizedContent(consent.content)"></div>

      <div v-if="localizedFileUrl" class="mt-3 pt-3" style="border-top: 1px solid #d9d9d9">
        <a
          :href="localizedFileUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10 9 9 9 8 9"/>
          </svg>
          {{ $t('consents.viewPdf') || 'View PDF' }}
        </a>
      </div>

      <div v-if="consent.questions && consent.questions.length > 0" class="mt-4 pt-3" style="border-top: 1px solid #d9d9d9">
        <p class="fw-bold mb-3">{{ $t('consents.pleaseAnswerQuestions') }}</p>

        <div
          v-for="question in consent.questions"
          :key="question.id"
          class="p-3 mb-3"
          :class="question.required && !isQuestionAnswered(question.id) && !isAlreadyAnswered
            ? 'border border-danger'
            : 'border'"
          style="border-color: #d9d9d9"
        >
          <p class="fw-bold mb-1">
              {{ getLocalizedContent(question.title) }}
            <span v-if="question.required" class="text-danger">*</span>
          </p>
          <p v-if="question.description" class="text-gray font-12 mb-2">
              {{ getLocalizedContent(question.description) }}
            </p>

          <div class="form-check mt-2">
              <input
              class="form-check-input"
                type="checkbox"
              :id="`question-${question.id}`"
                :checked="isQuestionAnswered(question.id)"
              :disabled="!isRead && !isAlreadyAnswered"
                @change="handleQuestionAnswer(question.id, $event.target.checked)"
            />
            <label
              class="form-check-label"
              :for="`question-${question.id}`"
              :class="{ 'text-gray': !isRead && !isAlreadyAnswered }"
            >
                {{ $t('consents.iAccept') }}
            </label>
          </div>

          <small v-if="!isRead && !isAlreadyAnswered" class="text-gray">
            {{ $t('consents.scrollToUnlock') }}
          </small>
          <small
            v-else-if="question.required && !isQuestionAnswered(question.id) && !isAlreadyAnswered"
            class="text-danger"
          >
            {{ $t('consents.fieldRequired') }}
          </small>
        </div>
      </div>
      
      <div v-if="!isScrolledToBottom" class="py-2 text-center" style="background: #f3f3f3; border-top: 1px solid #d9d9d9; margin-top: 1rem">
        <small class="text-gray">{{ $t('consents.scrollToContinue') }}</small>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from "vue";
import { defineProps, defineEmits } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
  consent: { type: Object, required: true },
  isRead: { type: Boolean, default: false },
  questionAnswers: { type: Object, default: () => ({}) },
  /** When true (e.g. required consent modal), cap scroll area on mobile so footer actions stay usable. */
  constrainForModal: { type: Boolean, default: false },
});

const emit = defineEmits(["read", "question-answer"]);

const { locale } = useI18n();
const contentRef = ref(null);
const isScrolledToBottom = ref(false);

const handleScroll = () => {
  if (!contentRef.value) return;
  const { scrollTop, scrollHeight, clientHeight } = contentRef.value;
  const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 5;
  if (isAtBottom && !isScrolledToBottom.value) {
    isScrolledToBottom.value = true;
    if (!props.isRead) emit("read", props.consent.id);
  } else if (!isAtBottom) {
    isScrolledToBottom.value = false;
  }
};

const getLocalizedContent = (content) => {
  if (!content) return "";
  if (typeof content === "string") return content;
  if (typeof content === "object") {
    const currentLocale = locale.value || "en-GB";
    if (content[currentLocale]) return content[currentLocale];
    const langCode = currentLocale.split("-")[0];
    const langKey = Object.keys(content).find((k) => k.startsWith(langCode));
    if (langKey) return content[langKey];
    return Object.values(content)[0] || "";
  }
  return "";
};

const localizedFileUrl = computed(() => {
  const urls = props.consent.filesUrls || {};
  if (typeof urls !== "object" || Object.keys(urls).length === 0) return null;
  const currentLocale = locale.value || "en-GB";
  if (urls[currentLocale]) return urls[currentLocale];
  const langCode = currentLocale.split("-")[0];
  const langKey = Object.keys(urls).find((k) => k.startsWith(langCode));
  if (langKey) return urls[langKey];
  return Object.values(urls)[0] || null;
});

const isAlreadyAnswered = computed(() => props.consent.answered === true);

const isQuestionAnswered = (questionId) => {
  if (props.questionAnswers[questionId] !== undefined) {
    return props.questionAnswers[questionId] === true;
  }
  // fallback: check per-question answer from the consent object
  const question = props.consent.questions?.find((q) => q.id === questionId);
  if (question?.answer !== null && question?.answer !== undefined) {
    return question.answer.accepted === true;
  }
  return false;
};

const pendingRequiredCount = computed(() => {
  if (isAlreadyAnswered.value) return 0;
  return (props.consent.questions || []).filter(
    (q) => q.required && !isQuestionAnswered(q.id)
  ).length;
});

const handleQuestionAnswer = (questionId, answered) => {
  emit("question-answer", { consentId: props.consent.id, questionId, answered });
};

const checkScrollPosition = () => {
  nextTick(() => {
    if (!contentRef.value) return;
    const { scrollHeight, clientHeight } = contentRef.value;
    if (scrollHeight <= clientHeight) {
        isScrolledToBottom.value = true;
      if (!props.isRead) emit("read", props.consent.id);
      } else {
        handleScroll();
    }
  });
};

watch(() => props.consent.id, () => {
  isScrolledToBottom.value = false;
  nextTick(() => {
    if (!contentRef.value) return;

    contentRef.value.scrollTop = 0;

    window.scrollTo({ top: 0, behavior: "auto" });
  });
  checkScrollPosition();
});

onMounted(() => checkScrollPosition());
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.consent-details-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  overflow: hidden;
}

.consent-content {
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 55vh;
  min-height: 200px;
  -webkit-overflow-scrolling: touch;
  word-wrap: break-word;
  overflow-wrap: break-word;

  :deep(table) {
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

@media screen and (max-width: $mdBreakpoint) {
  .consent-content {
    max-height: none;
    min-height: auto;
    overflow-y: visible;
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }

  .consent-content--modal-mobile {
    max-height: min(48vh, 380px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
}
</style>
